<script lang="ts" setup>
import AUApi from "@/api";
import type { DateParseReponse } from "./CalenderHelper";
import { getDateFromInput, getMask, valueToFormatStr } from "./CalenderHelper";
import { scrollIntoView } from "@/UIHelper";

import { ref, useTemplateRef, onMounted, reactive, watch } from "vue";
import {
  CommonEditControlEmits,
  EditControlProps,
  getDefaultEditorControlConfig,
  mounted,
  valueChanged,
} from "@/editor/EditComponentStatic";
import MaskedInput from "./MaskedInput.vue";
import { useRegisterValidation } from "@/components/EditorValidator";
declare interface EditTimePickerControlProps extends EditControlProps {
  formatProp?: string;
  onFocus?: boolean;
  timeDate?: string;
}

declare interface EditTimePickerUiData extends EditorControlUiData {
  menuLeft: number; // = 0;
  menuTop: number; // = 0;
  showPicker: boolean; // = false;
  showField: boolean; // = true;
  inputText: string; // = "";
  mask: string; // = "D1.M1.1111";
  inputDate: Date; // = ""
  format1: string; // = "dd.MM.yyyy"; // übergabe Format und ANZEIGE Format Muss ident sein!
  format: string; // = "dd.MM.yyyy";
  inputFormat: string; // = null; // auf maske abgestimmter formatstr
  strict: boolean; // = true;
  start: string; // = "Month";
  depth: string; // = "Year";
  showLabel: boolean; // = false;
  initalized: boolean; // = false;
  iconCss?: string;

  // Datum für Picker
  currentDate?: string; // string des datums
  currentDateValue?: Date; // das echte datum

  minDate?: Date; // = ""; // auf SperrDAtum setzen
  maxDate?: Date; // = "";
  holidayDate: Holiday[]; // = []; // muss mit den Datums aus der Holiday tabelle gefüllt werden
  isFocused?: boolean;
  //errorMessages: Array<string> = [];
  text?: string;
  sperrBegriff?: string;
  maxHours?: number;
}

const props = defineProps<EditTimePickerControlProps>();
const emitFn = defineEmits<CommonEditControlEmits>();
const maskedControl = useTemplateRef("masked");

const { config, ui } = getDefaultEditorControlConfig<
  EditorControlConfig,
  EditTimePickerControlProps,
  EditTimePickerUiData
>(props, emitFn, {
  // default start values
  menuLeft: 0,
  menuTop: 0,
  showPicker: false,
  showField: true,
  mask: "HH:M0",
  format1: "HH:mm", // übergabe Format und ANZEIGE Format Muss ident sein!
  format: "HH:mm",
  strict: true,
  start: "Month",
  depth: "Year",
  showLabel: false,
  initalized: false,

  holidayDate: [], // muss mit den Datums aus der Holiday tabelle gefüllt werden
  errorMessages: [],
});
export interface TimeEntry {
  text: string;
  hour: number;
  min: number;
  delta: number;
}
const uiData = reactive(ui);
const times: TimeEntry[] = [];
const hourparts: number = 2; // da einstellen ob 2 oder 4 teile (30 min oder 15 min)
let selectedItem: TimeEntry | undefined;

function zeroPad(num: number, places: number) {
  return String(num).padStart(places, "0");
}
let date: Date = new Date(new Date().toISOStringWithTZ().substring(0, 10));

config.validate = validate;
useRegisterValidation({ config });

onMounted(async () => {
  mounted(uiData, props, config);
  await mountedBaseDatePicker();

  let span = new Date().getTimezoneOffset(); // Zeitzohne dazu rechenn...
  if (config.column?.datatype === "Zeit") {
    span = 0; // bei NUR Uhrzeit nicht dazu rechnen!
  }
  for (let hour = 0; hour < 24; hour++) {
    for (let min = 0; min < 60; min += 60 / hourparts) {
      times.push({
        text: zeroPad(hour, 2) + ":" + zeroPad(min, 2),
        hour,
        min,
        delta: hour * 60 + min + span,
      });
    }
  }
  date = new Date(new Date().toISOStringWithTZ().substring(0, 10)); // today
  if (uiData.currentDateValue) {
    // currentDate ;)
    if (!uiData.currentDateValue.toISOStringWithTZ) {
      uiData.currentDateValue = new Date(uiData.currentDateValue);
    }
    date = new Date(
      uiData.currentDateValue.toISOStringWithTZ().substring(0, 10),
    );
  }
  // setzt den Cursor an den Anfang - das man auch bei vorschlagswert schnell editieren kann!
  if (props.autofocus) {
    setTimeout(() => {
      const anyMasked: any = maskedControl.value as any;
      if (anyMasked && typeof anyMasked.setCursorToStart === "function") {
        anyMasked.setCursorToStart();
      }
    }, 0);
  }
});

watch(
  () => config.initValue,
  (newVal) => {
    mountedBaseDatePicker();
  },
);

// Expose focus to allow parents to programmatically focus the input
function focus() {
  // try focusing the underlying MaskedInput component
  const anyMasked: any = maskedControl.value as any;
  if (anyMasked && typeof anyMasked.focus === "function") {
    anyMasked.focus();
  }
}
defineExpose({ focus });
async function mountedBaseDatePicker() {
  uiData.format = "HH:mm";
  if (props.formatProp) {
    uiData.format = props.formatProp;
  }

  uiData.mask = getMask(uiData.format);
  let date: any = null;
  const initValue = config.initValue;

  if (initValue) {
    if (typeof initValue === "string") {
      // wenn string dan hoffentlich ein iso
      date = new Date(initValue);
      if (isNaN(date)) {
        // ungültige ignorieren wir ;)
        date = null;
      }
    } else if (
      // schaun obs ein gültiges datum is
      Object.prototype.toString.call(date) === "[object Date]" &&
      !isNaN(date)
    ) {
      date = initValue;
    } else if (config.column) {
      if (config.column.meta.neuArt && config.column.meta.neuArt === "Sync") {
        // Datum auf VON EditZeitbereich setzen....???
        date = new Date().toISOStringWithTZ();
      } else if (config.column.meta.auto && initValue === undefined) {
        // Datum auf VON EditZeitbereich setzen....
        date = new Date().toISOStringWithTZ();
      } else if (config.column.type === "mass") {
        // bei Mass muss es in HH:mm angezeigt werden - aber es kommt zB als 1 - für 01:00
        if (initValue && typeof initValue === "object") {
          date = initValue.value;
        } else if (initValue && typeof initValue === "number") {
          date = initValue;
        }
        if (initValue.faktor > 0) {
          date = date / initValue.faktor;
        }
        if (date && date > 0) {
          let hour = Math.floor(date);
          let min = Math.round((date - hour) * 60);
          if (min === 60) {
            hour = 1;
            min = 0;
          }
          uiData.inputText = zeroPad(hour, 2) + ":" + zeroPad(min, 2);
          // value Changed muss aufgerufen werden, damit der Wert zum Speichern sauber gesetzt ist!
          // auf nicht Init Setzen, damit die Daten fürs Speichern initalisiert werden!
          valueChanged(config, uiData, date, false);
          return;
        } else {
          date = initValue;
        }
        // date = initValue;
      }
    }
  }
  // Maskierung ermittlen und Wert passend formatieren...
  if (date) {
    uiData.showLabel = true;
    uiData.currentDateValue = date;
    uiData.inputText = uiData.currentDate = valueToFormatStr(
      date,
      uiData.format,
    );
  } else {
    uiData.currentDateValue = undefined;
    uiData.inputText = uiData.currentDate = "";
  }
}

function validate() {
  if (uiData.disabled) {
    uiData.errorText = "";
    return true;
  } // disabled Controls sind IMMER valide ;)
  if (uiData.required) {
    if (uiData.currentDateValue && uiData.inputText) {
      uiData.errorText = "";
      return true;
    }
    uiData.errorText = config.requiredMessage;
    return false;
  }
  uiData.errorText = "";
  return true;
}

function clickAppendTime() {
  selectedItem = times.find(
    (t) => t.text === uiData.inputText.substring(0, 2) + ":00",
  );
  clickAppend();
}
function parseTimeString(x: string) {
  const contents = x.split(":");
  const hour = parseInt(contents[0], 10);
  const min = parseInt(contents[1], 10);
  return hour + min / 60;
}
function changedTime(d: { event: Event; element: HTMLElement }) {
  try {
    if (d && d.event && d.event.type === "blur") {
      return;
    }
    let val: DateParseReponse | undefined;
    let valString;
    let isEmpty = true;
    let isNotFull = true;
    if (uiData.inputText) {
      isEmpty = uiData.inputText.replace(/\s:-/g, "").length === 0;
      const position = uiData.inputText.indexOf(":");
      isNotFull = uiData.inputText.indexOf(":") <= 0;
      if (config && config.column && config.column.type === "mass") {
        valString = parseTimeString(uiData.inputText);
        //   if (props.minHours && (val ?? 0) < props.minHours) {
        //   uiData.errorText = "zu klein";
        //   return;
        // }
        // if (uiData.maxHours && (val ?? 0) > uiData.maxHours) {
        //   uiData.errorText = "zu groß";
        //   return;
        // }

        if (isNotFull || isEmpty) {
          if (uiData.required) {
            uiData.errorText = config.requiredMessage;
          }
          valueChanged(config, uiData, null);
          return;
        } else {
          valueChanged(config, uiData, valString);
          return;
        }
      } else {
        val = getDateFromInput(uiData.inputText, uiData.format);
      }
    }
    if (isNotFull || isEmpty) {
      if (uiData.required) {
        uiData.errorText = config.requiredMessage;
      }
      valueChanged(config, uiData, null);
      return;
    }

    var newDate: Date | undefined;
    if (val) {
      if (val.date) {
        const hour = val.date.getHours();
        const min = val.date.getMinutes();
        newDate = new Date(date.valueOf());
        newDate.setHours(hour);
        newDate.setMinutes(min);
        newDate.setSeconds(0);
        newDate.setMilliseconds(0);
      }

      if (newDate) {
        newDate = checkTimeDate(newDate);
      }
      uiData.currentDateValue = newDate;
    }

    if (newDate) {
      uiData.showField = false;
      // inputText = val.toISOStringWithTZ();

      valueChanged(config, uiData, newDate.toISOStringWithTZ());
      uiData.showField = true;
    } else {
      valueChanged(config, uiData, null);
    }
  } catch (err) {
    console.error(err);
  } finally {
    validate();
  }
}

function checkTimeDate(val: Date) {
  if (!val) {
    return undefined;
  }

  if (props.timeDate) {
    console.log("setTimedate: " + val.toISOStringWithTZ());
    let x: any = props.timeDate;
    if (x.toISOStringWithTZ) {
      x = x.toISOStringWithTZ().substring(0, 10);
    }
    x = new Date(x);
    x.setHours(val.getHours());
    x.setMinutes(val.getMinutes());
    val = x; // val muss dann auf das neu berechnet Datum gesetzt werden!
    console.log("fixed setTimedate: " + val.toISOStringWithTZ());
  }
  return val;
}

function setTime(item: TimeEntry) {
  let dateLocal: Date | undefined;
  let valString;
  if (config.column?.datatype === "Zeit") {
    date = new Date(1970, 0, 1);
    dateLocal = date;
    let delta = item.delta;
    if (delta < 0) {
      delta = delta * -1;
    }
    dateLocal = dateLocal.addMinutes(delta);
  } else if (config?.column?.type === "mass") {
    valString = parseTimeString(item.text);
    uiData.inputText = item.text;
    valueChanged(config, uiData, valString);
    uiData.showPicker = false;
    return;
  } else {
    dateLocal = date;
    dateLocal = dateLocal.addMinutes(item.delta);
  }

  uiData.currentDateValue = dateLocal;
  uiData.showField = false;
  uiData.inputText = valueToFormatStr(dateLocal, uiData.format);
  dateLocal = checkTimeDate(dateLocal);
  setTimeout(() => {
    uiData.errorText = ""; // muss valide sein - is ja ausgesucht ;)
    uiData.showField = true;
    if (dateLocal) {
      console.log("time changed: " + dateLocal.toISOStringWithTZ());
      valueChanged(config, uiData, dateLocal.toISOStringWithTZ());
    } else {
      console.log("time changed: null");
      valueChanged(config, uiData, null);
    }
  }, 50);

  uiData.showPicker = false;
}
function clickAppend() {
  uiData.showPicker = !uiData.showPicker;
}

function disabledText() {
  //      return this.valueToFormatStr(this.currentDate, uiData.format);
  if (uiData.currentDateValue) {
    return valueToFormatStr(uiData.currentDateValue, uiData.format);
  } else {
    return valueToFormatStr(uiData.data.value, uiData.format);
  }
}
</script>
<template>
  <div class="wrap" v-if="uiData.showMutable" style="min-width: 150px">
    <v-tooltip
      :disabled="!uiData.showtooltip"
      location="bottom"
      max-width="300px"
      :text="uiData.tooltip"
    >
      <template v-slot:activator="{ props }">
        <MaskedInput
          :disabled="uiData.disabled"
          v-bind="props"
          :required="uiData.required"
          ref="masked"
          :label="uiData.description"
          :mask="uiData.mask"
          :placeholder="uiData.format"
          v-model="uiData.inputText"
          @change="changedTime"
          @update:modelValue="changedTime"
          :errorText="uiData.errorText"
          id="textfield"
          v-if="uiData.showField"
          @focus="scrollIntoView($event)"
          :autofocus="autofocus"
          @appendClick="uiData.showPicker = !uiData.showPicker"
        >
        </MaskedInput>

        <ValueChangeTracker
          v-if="
            (uiData.isChanged || props.disableValueChangeTracker) &&
            !uiData.disabled
          "
        />
      </template>
    </v-tooltip>
  </div>
</template>
<style scoped>
label.e-float-text,
.e-float-input label.e-float-text,
.e-float-input.e-control-wrapper label.e-float-text,
.e-float-input:not(.e-input-focus)
  input:not(:focus):valid
  ~ label.e-float-text.e-label-bottom,
.e-float-input.e-control-wrapper:not(.e-input-focus)
  input:not(:focus):valid
  ~ label.e-float-text.e-label-bottom {
  top: -20px;
}

.e-input-group input.e-input,
.e-input-group.e-control-wrapper input.e-input {
  font-size: 13px;
  font-family: "Roboto", sans-serif;
  padding-bottom: 0px;
}
</style>
